import { Injectable, signal } from '@angular/core';
import {
	ISearchSymbol,
	SearchCategory,
	SearchCategoryDefaultScores,
	SortKeys,
	SymbolSectorModel
} from '../models/search-symbol.model';
import { normalizeTitle, normalizeTitleKeepSpace } from '../../../tools/normalize-title';
import { SymbolService } from '../../shared/services/symbol.service';
import { ISymbol } from '../../shared/models/symbol.model';
import { BehaviorSubject, Subject, combineLatestWith, map } from 'rxjs';
import { pSortGeneric } from '../../../tools/p-sort';
import { Swipeable, SwipeState } from '../../swipe/settings';
import { GREEN, RED } from '../../../constants/symbol-state.constant';
import { containsNumbers, convertStringToNumber } from '../../../tools/primptives-tool/string-tool';
import { Fund, FundsFacade } from '../../fund';
import { LocalStorageService } from '../../shared/services';
import { IpoService, NoneEtf } from '@easy-lib/modules/market-watch';
import { toObservable } from '@angular/core/rxjs-interop';

const RecentSerachKey = 'RecentSearch';
const nonEtfSymbolGroup = 'nonEtfSymbolGroup';
@Injectable({
	providedIn: 'root'
	// providers: [FundsFacade]
})
export class SearchSymbolService {
	symbols: ISearchSymbol[] = [];
	carSymbols: ISearchSymbol[] = [];
	funds: ISearchSymbol[] = [];
	selectedSector = signal<SymbolSectorModel>(null);
	selectedSector$ = toObservable(this.selectedSector);
	titleExceptionList = ['IRT3TAZF0001'];
	private _sortKey: SortKeys = SortKeys.ByTotalTradeValue;

	set sortKey(val: SortKeys) {
		this._sortKey = val;
		this.sortKeySubject.next(val);
	}

	get sortKey(): SortKeys {
		return this._sortKey;
	}

	private _recentSymbols: ISearchSymbol[] = this.localStorage.getItem(RecentSerachKey)
		? JSON.parse(this.localStorage.getItem(RecentSerachKey))
		: [];
	private _sectorSymbolsfilterActive = false;
	private _sortByDescending = true;
	private selectedSearchedSymbol = new BehaviorSubject<ISearchSymbol>(null);
	// eslint-disable-next-line @typescript-eslint/member-ordering
	selectedSearchedSymbol$ = this.selectedSearchedSymbol.asObservable();
	setSelectedSearchedSymbol(symbol?: ISearchSymbol) {
		this.selectedSearchedSymbol.next(symbol);
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	private sortKeySubject = new Subject<SortKeys>();
	// eslint-disable-next-line @typescript-eslint/member-ordering
	sortKeyObs$ = this.sortKeySubject.asObservable();
	// eslint-disable-next-line @typescript-eslint/member-ordering
	private closeAllToolbars = new BehaviorSubject<string>(null);
	// eslint-disable-next-line @typescript-eslint/member-ordering
	closeAllToolbars$ = this.closeAllToolbars.asObservable();
	closeAllItemToolbars(symbolIsin?: string) {
		this.closeAllToolbars.next(symbolIsin);
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	private openNoteManagement = new Subject<string>();
	// eslint-disable-next-line @typescript-eslint/member-ordering
	openNoteManagement$ = this.openNoteManagement.asObservable();
	openNoteManagementHandler(symbolIsin?: string) {
		this.openNoteManagement.next(symbolIsin);
	}
	// eslint-disable-next-line @typescript-eslint/member-ordering
	private openConditionalTools = new Subject<string>();
	// eslint-disable-next-line @typescript-eslint/member-ordering
	openConditionalTools$ = this.openConditionalTools.asObservable();
	openConditionalToolsHandler(symbolIsin?: string) {
		this.openConditionalTools.next(symbolIsin);
	}

	constructor(
		private localStorage: LocalStorageService,
		private symbolService: SymbolService,
		private fundsFacade: FundsFacade,
		private ipoService: IpoService
	) {
		this.getStocks();

		this.fundsFacade.funds$
			.pipe(
				combineLatestWith(this.ipoService.noneEtfsObs$),
				map(([funds, noneEtf]) => {
					const fundsSearch = funds ? funds.map((fund: Fund) => this.convertFundsToSymbol(fund)) : [];
					const nonSearch = noneEtf ? noneEtf.map((fund: NoneEtf) => this.convertNonToSymbol(fund)) : [];
					this.funds = [...fundsSearch, ...nonSearch];
				})
			)
			.subscribe();
		this.getfunds();
	}

	get defaultSearchSymbols() {
		const defaultSearchSymbols = [...this.recentSymbols];

		this.symbolService.defaultSymbols.forEach(symbol => {
			if (!this.recentSymbols.some(val => val.symbolIsin === symbol.symbolIsin)) {
				defaultSearchSymbols.push(this.convertISymbolToISearchSymbol(symbol));
			}
		});

		defaultSearchSymbols.forEach((val, inx) => {
			val.listIndex = inx;
		});
		return defaultSearchSymbols;
	}

	get defaultSearchSymbolsWithCategory() {
		const defaultSearchSymbols = [...this.recentSymbols];
		const defaultSymbolsWithCategory = new Map<SearchCategory, ISearchSymbol[]>();

		this.symbolService.defaultSymbols.forEach(symbol => {
			if (!this.recentSymbols.some(val => val.symbolIsin === symbol.symbolIsin)) {
				defaultSearchSymbols.push(this.convertISymbolToISearchSymbol(symbol));
			}
		});

		defaultSearchSymbols.forEach((val, inx) => {
			val.listIndex = inx;
		});
		defaultSymbolsWithCategory.set(SearchCategory.Default, defaultSearchSymbols);
		return defaultSymbolsWithCategory;
	}

	get sectorStocksfilterActive(): boolean {
		// eslint-disable-next-line no-underscore-dangle
		return this._sectorSymbolsfilterActive;
	}

	get sortByDescending(): boolean {
		return this._sortByDescending;
	}

	public get recentSymbols(): ISearchSymbol[] {
		// eslint-disable-next-line no-underscore-dangle
		this._recentSymbols.forEach(val => {
			val.symbolNameInnerHtml = val.symbolName;
			val.symbolTitleInnerHtml = val.title;
			if (!val.swipeState) {
				val.swipeState = new Swipeable('', SwipeState.None);
			}
		});
		return this._recentSymbols;
	}

	public set recentSymbols(value: ISearchSymbol[]) {
		// eslint-disable-next-line no-underscore-dangle
		this._recentSymbols = value;
		if (value && value.length > 0) this.localStorage.setItem(RecentSerachKey, JSON.stringify(value));
		else this.localStorage.removeItem(RecentSerachKey);
	}

	toggleFilterActive(): void {
		this._sectorSymbolsfilterActive = !this._sectorSymbolsfilterActive;
	}

	toggleSortDirection(): void {
		this._sortByDescending = !this._sortByDescending;
		this.sortKeySubject.next(this.sortKey);
	}

	getStocks() {
		this.symbolService.symbols$.subscribe(symbol => {
			if (symbol) {
				this.symbols = [];
				symbol.forEach(symbol => {
					if (symbol.symbolIsin === 'IRT1STIL0001') {
						symbol.title = 'صندوق س صنایع فلزات مفید- بخشی';
					}
					this.symbols.push(this.convertISymbolToISearchSymbol(symbol));
				});
			}
		});
	}

	getfunds() {
		this.fundsFacade.loadAllFund({ hasErrorNotify: false }).subscribe((vals: Fund[]) => {
			if (vals?.length > 0) {
				this.funds = [...vals.map((fund: Fund) => this.convertFundsToSymbol(fund))];
			}
		});
	}

	setHilightforSearchedWords(
		symbol: ISearchSymbol,
		str: string,
		lastsearchedNameIndex: number,
		lastsearchedTitleIndex: number
	): {
		symbol: ISearchSymbol;
		lastsearchedNameIndex: number;
		lastsearchedTitleIndex: number;
	} {
		lastsearchedNameIndex = normalizeTitleKeepSpace(symbol.symbolNameInnerHtml).indexOf(
			normalizeTitleKeepSpace(str),
			lastsearchedNameIndex
		);

		lastsearchedTitleIndex = normalizeTitleKeepSpace(symbol.symbolTitleInnerHtml).indexOf(
			normalizeTitleKeepSpace(str),
			lastsearchedTitleIndex
		);
		if (lastsearchedNameIndex >= 0) {
			const spliced = symbol.symbolNameInnerHtml.split('').splice(lastsearchedNameIndex, str.length).join('');
			symbol.symbolNameInnerHtml =
				symbol.symbolNameInnerHtml.substring(0, lastsearchedNameIndex) +
				symbol.symbolNameInnerHtml
					.substring(lastsearchedNameIndex)
					.replace(
						spliced,
						`<span class="text-primary ${
							symbol.searchCategoryName === SearchCategory.cars
								? 'commodity-exchange-info-description-highlight'
								: ''
						} ">${spliced}</span>`
					);
		}

		if (lastsearchedTitleIndex >= 0) {
			const spliced = symbol.symbolTitleInnerHtml.split('').splice(lastsearchedTitleIndex, str.length).join('');
			symbol.symbolTitleInnerHtml =
				symbol.symbolTitleInnerHtml.substring(0, lastsearchedTitleIndex) +
				symbol.symbolTitleInnerHtml
					.substring(lastsearchedTitleIndex)
					.replace(
						spliced,
						`<span class="text-primary ${
							symbol.searchCategoryName === SearchCategory.cars
								? 'commodity-exchange-info-description-highlight'
								: ''
						} ">${spliced}</span>`
					);
		}
		return { symbol, lastsearchedNameIndex, lastsearchedTitleIndex };
	}

	searchStocks(
		input: string,
		skip: number,
		take: number,
		includeFunds: boolean,
		includeCars: boolean
	): ISearchSymbol[] {
		const originInputWords = this.getAllWords(input);
		let query = [
			...[...(includeFunds ? this.funds : []), ...(includeCars ? this.carSymbols : []), ...this.symbols].filter(
				symbol => {
					const res = this.simpleFilter(input, symbol);
					return res && symbol.isActive;
				}
			)
		];
		if (query.length === 0) {
			query = [
				...[
					...(includeFunds ? this.funds : []),
					...(includeCars ? this.carSymbols : []),
					...this.symbols
				].filter(symbol => {
					const res = this.advancedFilter(input, symbol, originInputWords);
					return res;
				})
			];
		}
		query = pSortGeneric<any>(query, 'symbolName');
		return query
			.filter((thing, i, arr) => arr.findIndex(t => t.symbolIsin === thing.symbolIsin) === i)
			.map(symbol => {
				const converted = this.scoreToSearchedSymbols(symbol, originInputWords);
				let highlightTools: {
					symbol: ISearchSymbol;
					lastsearchedNameIndex: number;
					lastsearchedTitleIndex: number;
				} = {
					symbol: converted,
					lastsearchedNameIndex: 0,
					lastsearchedTitleIndex: 0
				};

				converted.symbolNameInnerHtml = converted.symbolName;
				converted.symbolTitleInnerHtml = converted.title;

				for (const searchstr of originInputWords) {
					highlightTools = this.setHilightforSearchedWords(
						highlightTools.symbol,
						searchstr,
						highlightTools.lastsearchedNameIndex,
						highlightTools.lastsearchedTitleIndex
					);
				}

				return highlightTools.symbol;
			})
			.sort((a: ISearchSymbol, b: ISearchSymbol) => b.score - a.score || b.searchCount - a.searchCount)
			.slice(skip, skip + take);
	}

	searchStocksByCategoryResult(
		input: string,
		skip: number,
		take: number,
		includeFunds: boolean,
		includeCars: boolean
	): Map<SearchCategory, ISearchSymbol[]> {
		const searchResult = this.searchStocks(input, skip, take, includeFunds, includeCars);
		const mapResult = this.orderByCategory(searchResult);
		return mapResult;
	}

	orderByCategory(
		symbolArray: ISearchSymbol[],
		sortByDefaultCategoryOrder = false
	): Map<SearchCategory, ISearchSymbol[]> {
		const mapResult = new Map<SearchCategory, ISearchSymbol[]>();
		const categoryScores = new Map<SearchCategory, number>();
		symbolArray.forEach(val => {
			if (!mapResult.has(val.searchCategoryName)) {
				mapResult.set(val.searchCategoryName, []);
			}

			const targetArray: ISearchSymbol[] = mapResult.get(val.searchCategoryName);

			targetArray.push(val);
			mapResult.set(val.searchCategoryName, targetArray);
		});

		for (const category of mapResult.keys()) {
			categoryScores.set(
				category,
				mapResult.get(category)[0].score +
					+SearchCategoryDefaultScores[
						Object.keys(SearchCategory)[Object.values(SearchCategory).indexOf(category)]
					]
			);
		}

		const sortedMapResult = new Map<SearchCategory, ISearchSymbol[]>();

		if (sortByDefaultCategoryOrder) {
			for (const category of Object.values(SearchCategory)) {
				if (mapResult.get(category as SearchCategory)) {
					sortedMapResult.set(category as SearchCategory, mapResult.get(category as SearchCategory));
				}
			}
		} else {
			categoryScores[Symbol.iterator] = function* () {
				yield* [...this.entries()].sort((a, b) => b[1] - a[1]);
			};

			for (const [key] of categoryScores) {
				if (mapResult.get(key)) {
					sortedMapResult.set(key, mapResult.get(key));
				}
			}
		}

		let index = 0;
		sortedMapResult.forEach(stocks => {
			stocks.forEach(stock => {
				stock.listIndex = index++;
			});
		});

		return sortedMapResult;
	}

	addStockToRecentsByIsin(isin: string) {
		const symbol = this.symbolService.symbols.find(val => val.symbolIsin === isin);
		this.addStockToRecents(symbol as ISearchSymbol);
	}

	updateSearchedStockByIsin(isin: string): any {
		this.increaseSearchCount(isin);
	}

	addStockToRecents(stock: ISearchSymbol) {
		const index = this.recentSymbols?.findIndex(s => s.symbolIsin === stock.symbolIsin);
		if (index > -1) return;

		stock.symbolTitleInnerHtml = stock.title;
		stock.symbolNameInnerHtml = stock.symbolName;
		this.recentSymbols = [stock, ...this.recentSymbols];
		const l = this.recentSymbols.length;
		if (l > 3) {
			this.recentSymbols = this.recentSymbols.slice(0, 3);
		}
	}

	clearrecentSymbols() {
		this.recentSymbols = [];
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	isTextSymbolIsin(value: string): boolean {
		if ((value.startsWith('I') && value.length === 1) || value.startsWith('IR')) return true;
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	searchBySymbolIsin(value: string): ISearchSymbol[] {
		let resArray: ISearchSymbol[] = [];
		if (value)
			resArray = [
				...this.symbols
					.filter(symbol => symbol.symbolIsin.startsWith(value))
					.map(val => ({ ...val, score: 1 }))
					.slice(0, 100)
			];

		if (resArray?.length > 0) {
			return resArray;
		}

		return [];
	}

	searchBySymbolIsinFullModel(value: string): ISearchSymbol[] {
		let resArray: ISearchSymbol[] = [];
		if (value)
			resArray = [...this.symbols.filter(symbol => symbol.symbolIsin.startsWith(value))]
				.map(val => ({ ...val, score: 1 }))
				.slice(0, 100);

		if (resArray?.length > 0) {
			return resArray;
		}

		return [];
	}

	batchSearchBySymbolIsins(value: string[]): ISearchSymbol[] {
		let resArray: ISearchSymbol[] = [];
		if (value && value.length > 0) {
			resArray = [
				...this.symbols
					.filter(symbol => value.includes(symbol.symbolIsin))
					.map((val, inx) => ({
						...val,
						score: 1,
						listIndex: inx,
						symbolNameInnerHtml: val.symbolName,
						symbolTitleInnerHtml: val.title
					}))
			];
		}

		if (resArray?.length > 0) {
			return resArray;
		}
		return [];
	}

	searchBySymbolIsinCategorySplited(value: string): Map<SearchCategory, ISearchSymbol[]> {
		const resArray = this.searchBySymbolIsinFullModel(value);

		if (resArray?.length > 0) {
			return this.orderByCategory(resArray);
		}

		return null;
	}

	searchBySectorCategorySplited(sector: SymbolSectorModel): Map<SearchCategory, ISearchSymbol[]> {
		const resArray = [
			...this.symbols
				.filter(x => x.sector?.trim() === sector.sectorCode)
				.map(val => ({
					...val,
					score: 1,
					symbolNameInnerHtml: val.symbolName,
					symbolTitleInnerHtml: val.title
				}))
		];

		if (resArray?.length > 0) {
			let rawResult = this.orderByCategory(resArray, true);

			rawResult = this.sortSearchResult(rawResult);
			return rawResult;
		}
		return null;
	}

	sortSearchResult(values: Map<SearchCategory, ISearchSymbol[]>): Map<SearchCategory, ISearchSymbol[]> {
		// eslint-disable-next-line no-underscore-dangle
		const sortedItems = new Map<SearchCategory, ISearchSymbol[]>();
		for (const category of values.keys()) {
			let rawSymbols: ISearchSymbol[] = values.get(category);

			switch (this.sortKey) {
				case SortKeys.ByLastTradePriceVar: {
					rawSymbols.sort(this.predicateBy('priceVar'));
					if (this.sortByDescending) rawSymbols.reverse();
					rawSymbols.sort(this.sortByActiveColor('stateColor'));
					break;
				}
				case SortKeys.BySymbolName: {
					// rawSymbols.sort(this.predicateBy('symbolName'));
					rawSymbols = pSortGeneric<any>(rawSymbols, 'symbolName');
					if (this.sortByDescending) rawSymbols.reverse();
					break;
				}
				case SortKeys.ByTotalTradeValue: {
					rawSymbols.sort(this.predicateBy('totalNumberOfSharesTraded'));
					if (this.sortByDescending) rawSymbols.reverse();
					rawSymbols.sort(this.sortByActiveColor('stateColor'));
					break;
				}
			}
			sortedItems.set(category, rawSymbols);
		}
		let i = 0;
		for (const symbols of sortedItems.values()) {
			symbols.forEach(symbol => {
				symbol.listIndex = i++;
			});
		}
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return sortedItems;
	}

	private predicateBy(prop: string) {
		return (a, b) => a[prop] - b[prop];
	}

	private sortByActiveColor(stateColor: string) {
		return (a, b) => {
			if (a[stateColor] === GREEN && b[stateColor] !== GREEN) return -1;
			if (a[stateColor] === RED && b[stateColor] !== RED) return 1;
			if (a[stateColor] === GREEN && b[stateColor] === GREEN) return 0;
		};
	}

	private predicateByDesc(prop: string) {
		return (a, b) => b[prop] - a[prop];
	}

	private increaseSearchCount(isin: string): any {
		const inx = this.symbols.findIndex(s => s.symbolIsin === isin);
		this.symbols[inx].searchCount++;
	}

	private getAllWords(text: string) {
		const allWordsIncludingDups = text.split(' ');
		const wordSet = allWordsIncludingDups.reduce((prev, current) => {
			if (current !== '‌' && current !== '') {
				const currentWithoutSemiSpace = current.replace('‌', ''); // حذف نیم فاصله از کلمه‌ی جاری
				prev[currentWithoutSemiSpace] = true;
			}
			return prev;
		}, {});
		return Object.keys(wordSet);
	}

	private convertISymbolToISearchSymbol(symbol: ISymbol): ISearchSymbol {
		return {
			...symbol,
			searchCount: 0,
			titleWords: symbol.title.split(' '),
			symbolNameInnerHtml: symbol.symbolName,
			symbolTitleInnerHtml: symbol.title,
			titleCorrected: symbol.title,
			searchCategoryName: this.symbolService.getCategoryByIsin(symbol.symbolIsin),
			swipeState: new Swipeable('', SwipeState.None)
		} as ISearchSymbol;
	}

	private convertFundsToSymbol(fund: Fund): ISearchSymbol {
		return {
			symbolIsin: fund.code,
			symbolName: fund.name,
			searchCount: 0,
			titleWords: [],
			symbolNameInnerHtml: fund.name,
			symbolTitleInnerHtml: 'صندوق صدور و ابطالی مفید',
			titleCorrected: 'صندوق صدور و ابطالی مفید',
			title: 'صندوق صدور و ابطالی مفید',
			sector: '',
			searchCategoryName: SearchCategory.funds,
			swipeState: new Swipeable('', SwipeState.None)
		} as ISearchSymbol;
	}

	private convertNonToSymbol(fund: NoneEtf): ISearchSymbol {
		return {
			symbolIsin: fund.fundCode,
			symbolName: fund.fundName,
			searchCount: 0,
			titleWords: [],
			symbolNameInnerHtml: fund.fundName,
			symbolTitleInnerHtml: 'صندوق صدور و ابطالی مفید',
			titleCorrected: 'صندوق صدور و ابطالی مفید',
			title: 'صندوق صدور و ابطالی مفید',
			sector: '',
			searchCategoryName: SearchCategory.funds,
			swipeState: new Swipeable('', SwipeState.None),
			symbolGroup: nonEtfSymbolGroup
		} as ISearchSymbol;
	}

	private scoreToSearchedSymbols(symbol: ISearchSymbol, searchStrings: string[]): ISearchSymbol {
		if (symbol.searchCategoryName === SearchCategory.cars) return symbol;
		symbol.score =
			searchStrings.length === 1
				? this.getSingleWordSearchScoreForStock(symbol, searchStrings[0])
				: this.getMultiWordSearchScoreForStock(symbol, searchStrings);
		return symbol;
	}

	private getSingleWordSearchScoreForStock(stock: ISearchSymbol, searchString: string): number {
		const nonEtfScore = 10000; // score for mathcing initial letters of symbol
		const specialStockScore = 200; // score for mathcing initial letters of symbol
		const symbolInitMatchScore = 5000; // score for mathcing initial letters of symbol
		const symbolInitStartWithScore = 2000; // score for mathcing initial letters of symbol
		const symbolMiddleMatchScore = 1000; // max score for mathcing middle letters of symbol
		const symbolMiddleMatchPenalty = 100; // penalty for each letter distance from the second letter of symbol, when matching
		const symbolSharedDueDateScore = -1;
		const normalSymbolInitMatchScore = 800; // score for mathcing initial letters of symbol
		const normalSymbolInitStartWithScore = 150; // score for mathcing initial letters of symbol
		const normalSymbolMiddleMatchScore = 50; // max score for mathcing middle letters of symbol
		const normalSymbolMiddleMatchPenalty = 4; // penalty for each letter distance from the second letter of symbol, when matching

		const titleInitMatchScore = 1000; // score for mathcing initial letters of title
		const titleInitStartWithScore = 200; // score for mathcing initial letters of title
		const titleMiddleMatchScore = 100; // max score for mathcing middle letters of title
		const titleMiddleMatchPenalty = 3; // penalty for each letter distance from the second letter of title, when matching

		const normalTitleInitMatchScore = 100; // score for mathcing initial letters of title
		const normalTitleInitStartWithScore = 10; // score for mathcing initial letters of title
		const normalTitleMiddleMatchScore = 5; // max score for mathcing middle letters of title
		const normalTitleMiddleMatchPenalty = 2; // penalty for each letter distance from the second letter of title, when matching

		const symbolIndex = stock.symbolName.toString().indexOf(searchString);
		const titleIndex = stock.title.toString().indexOf(searchString);
		const noramlSymbolIndex = normalizeTitle(stock.symbolName.toString()).indexOf(normalizeTitle(searchString));
		const normalTitleIndex = normalizeTitle(stock.title.toString()).indexOf(normalizeTitle(searchString));

		const symbolMatchScore = stock.symbolName === searchString ? symbolInitMatchScore : 0;
		const symbolStartWithScore = symbolIndex === 0 ? symbolInitStartWithScore : 0;
		const symbolIncludeScore =
			symbolIndex > 0
				? symbolMiddleMatchScore -
					symbolIndex * symbolMiddleMatchPenalty -
					Math.abs(stock.symbolName.length - searchString.length)
				: 0;

		const symbolScore = symbolMatchScore + symbolStartWithScore + symbolIncludeScore;

		const normalSymbolMatchScore =
			normalizeTitle(stock.symbolName) === normalizeTitle(searchString) ? normalSymbolInitMatchScore : 0;
		const normalSymbolStartWithScore = noramlSymbolIndex === 0 ? normalSymbolInitStartWithScore : 0;
		const normalSymbolIncludeScore =
			noramlSymbolIndex > 0
				? normalSymbolMiddleMatchScore -
					noramlSymbolIndex * normalSymbolMiddleMatchPenalty -
					Math.abs(stock.symbolName.length - searchString.length)
				: 0;

		const normalSymbolScore = normalSymbolMatchScore + normalSymbolStartWithScore + normalSymbolIncludeScore;

		const titleMatchScore = stock.title === searchString ? titleInitMatchScore : 0;
		const titleStartWithScore = titleIndex === 0 ? titleInitStartWithScore : 0;
		const titleIncludeScore = titleIndex > 0 ? titleMiddleMatchScore - titleIndex * titleMiddleMatchPenalty : 0;

		const titleScore = titleMatchScore + titleStartWithScore + titleIncludeScore;

		const normalTitleMatchScore =
			normalizeTitle(stock.title) === normalizeTitle(searchString) ? normalTitleInitMatchScore : 0;
		const normalTitleStartWithScore = normalTitleIndex === 0 ? normalTitleInitStartWithScore : 0;
		const normalTitleIncludeScore =
			normalTitleIndex > 0 ? normalTitleMiddleMatchScore - normalTitleIndex * normalTitleMiddleMatchPenalty : 0;

		const normalTitleScore = normalTitleMatchScore + normalTitleStartWithScore + normalTitleIncludeScore;
		const sharedDueDateScore = stock.shareDueDate
			? new Date(stock.shareDueDate).getTime() * symbolSharedDueDateScore
			: 0;
		const totalScore =
			symbolScore +
			titleScore +
			normalSymbolScore +
			normalTitleScore +
			(stock.symbolGroup === nonEtfSymbolGroup ? nonEtfScore : 0) +
			(this.specialScoreSymbolIsins.includes(stock.symbolIsin) ? specialStockScore : 0) +
			sharedDueDateScore;

		return totalScore + (containsNumbers(stock.symbolName) ? convertStringToNumber(stock.symbolName) : 0);
	}

	specialScoreSymbolIsins = [
		'IRT3AVNF0001',
		'IRT1STIL0001',
		'IRT1ARAM0001',
		'IRT3SATF0001',
		'IRTKMOFD0001',
		'IRT3TVAF0001'
	];

	private getMultiWordSearchScoreForStock(stock: ISearchSymbol, searchStrings: string[]): number {
		let totalScore = 0;
		for (const searchstr of searchStrings) {
			totalScore = totalScore + this.getSingleWordSearchScoreForStock(stock, searchstr);
		}
		return totalScore;
	}

	private advancedFilter(input: string, symbol: ISearchSymbol, inputWords: string[]) {
		let regExp = new RegExp(input);
		let regExpNormalize = new RegExp(normalizeTitle(input));

		const inputWordsLength = inputWords.length;
		// #region normal Test
		let res = regExp.test(symbol.symbolName);

		if (res) {
			return res;
		}
		// #endregion normal Test

		// #region if it is just one word => فرآور
		if (inputWords.length === 1) {
			regExp = new RegExp(inputWords[0]);
			regExpNormalize = new RegExp(normalizeTitle(inputWords[0]));

			// const normalizedRegExp = new RegExp(new RegExp(normalizeTitle(inputWords[0])));
			if (symbol.searchOptimizeTitle) {
				res = regExp.test(symbol.titleCorrected) && !this.titleExceptionList.includes(symbol.symbolIsin);
				if (res) {
					return res;
				}
			}

			// new RegExp(normalizeTitle(inputWords[0])).test(s.symbolName) here for catch words like اهرم when typing آهرم
			res =
				regExp.test(symbol.symbolName) ||
				(regExp.test(symbol.titleCorrected) && !this.titleExceptionList.includes(symbol.symbolIsin)) ||
				regExpNormalize.test(symbol.symbolName) ||
				(regExpNormalize.test(symbol.titleCorrected) && !this.titleExceptionList.includes(symbol.symbolIsin));
			if (res) {
				return res;
			}
		}
		// #endregion if it is just one word => فرآور

		// #region if it is more than one word => نیرو محرکه
		for (let i = 0; i < inputWordsLength; i++) {
			regExp = new RegExp(inputWords[i]);
			regExpNormalize = new RegExp(normalizeTitle(inputWords[i]));
			res =
				res ||
				regExp.test(symbol.symbolName) ||
				(regExp.test(symbol.titleCorrected) && !this.titleExceptionList.includes(symbol.symbolIsin)) ||
				regExpNormalize.test(symbol.symbolName) ||
				(regExpNormalize.test(symbol.titleCorrected) && !this.titleExceptionList.includes(symbol.symbolIsin));
		}
		// #endregion if it is more than one word => نیرو محرکه

		// #region if it is more than one word and we want to combine the titleCorrected and searchOptimizeTitle
		// نیرو     محرکه or فر    آور
		regExp = new RegExp(inputWords.join(' '));
		regExpNormalize = new RegExp(normalizeTitle(inputWords.join(' ')));

		res =
			res ||
			regExp.test(symbol.searchOptimizeTitle) ||
			regExp.test(symbol.symbolName) ||
			(regExp.test(symbol.titleCorrected) && !this.titleExceptionList.includes(symbol.symbolIsin)) ||
			regExpNormalize.test(symbol.searchOptimizeTitle) ||
			regExpNormalize.test(symbol.symbolName) ||
			(regExpNormalize.test(symbol.titleCorrected) && !this.titleExceptionList.includes(symbol.symbolIsin));
		// #endregion if it is more than one word and we want to combine the titleCorrected and searchOptimizeTitle
		return res;
	}

	private simpleFilter(input: string, symbol: ISearchSymbol) {
		const regExp = new RegExp(input);
		const regExpNormaliaze = new RegExp(normalizeTitle(input));
		// #region normal Test
		const res =
			regExp.test(symbol.symbolName) ||
			regExpNormaliaze.test(normalizeTitle(symbol.symbolName)) ||
			(regExp.test(symbol.titleCorrected) && !this.titleExceptionList.includes(symbol.symbolIsin));

		if (res) {
			return res;
		}
		// #endregion normal Test
		return res;
	}
}
